import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useNewsletterSubscribe } from './NewsletterInlineCard';
import { Mail, X, Loader2, CheckCircle2 } from 'lucide-react';

import { t } from '@/i18n';
const POPUP_STORAGE_KEY  = 'aan_newsletter_popup_state'; // { dismissedAt } | 'signed_up'
const STICKY_STORAGE_KEY = 'aan_newsletter_sticky_state';
const SIGNED_UP_KEY      = 'aan_newsletter_signup';
const POPUP_DELAY_MS     = 15_000;
const SNOOZE_DAYS        = 14;

/**
 * Two capture surfaces share this file because they share state:
 *   1. Timed pop-up — fires after ~15s on any marketing page (once per 14 days)
 *   2. Sticky bottom bar — always visible, dismissible, snoozed 14 days on close
 *
 * Both suppress themselves after a successful signup and check a shared
 * `aan_newsletter_signup` localStorage key so the user only ever gets one ask.
 */

function isSnoozed(key) {
  try {
    if (localStorage.getItem(SIGNED_UP_KEY) === '1') return true;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    if (raw === 'signed_up') return true;
    const { dismissedAt } = JSON.parse(raw);
    if (!dismissedAt) return false;
    const days = (Date.now() - dismissedAt) / 86_400_000;
    return days < SNOOZE_DAYS;
  } catch { return false; }
}

function snooze(key) {
  try { localStorage.setItem(key, JSON.stringify({ dismissedAt: Date.now() })); } catch { /* ignore */ }
}

// --------------------------------------------------------------------------
// Popup
// --------------------------------------------------------------------------

export const NewsletterPopup = () => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const { status, error, submit } = useNewsletterSubscribe('popup');

  useEffect(() => {
    if (isSnoozed(POPUP_STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), POPUP_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    setOpen(false);
    if (status !== 'success') snooze(POPUP_STORAGE_KEY);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const r = await submit({ email, first_name: firstName });
    if (r.ok) {
      try { localStorage.setItem(POPUP_STORAGE_KEY, 'signed_up'); } catch { /* ignore */ }
      setTimeout(() => setOpen(false), 2200);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
         style={{ background: 'rgba(15, 23, 42, 0.65)' }}
         onClick={close}
         data-testid="newsletter-popup-backdrop"
    >
      <div className="relative w-full max-w-md rounded-2xl overflow-hidden shadow-2xl"
           onClick={(e) => e.stopPropagation()}
           style={{ background: 'linear-gradient(135deg, #0B1220 0%, #0F172A 55%, #1E293B 100%)' }}
           data-testid="newsletter-popup">
        <button onClick={close} aria-label={t("Close")}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center text-white"
                data-testid="newsletter-popup-close">
          <X className="h-4 w-4" />
        </button>

        <div className="p-7 pt-9">
          <div className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-[#F97316] mb-3">
            <Mail className="h-3.5 w-3.5" /> {t("Free monthly newsletter")}
          </div>
          <h3 className="text-2xl font-bold text-white leading-tight mb-2">
            {t("Get the lien-law plays that get contractors paid.")}
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed mb-5">
            {t("One email a month. State deadline changes, notice pitfalls, and real payment wins from the AAN desk.")}
          </p>

          {status === 'success' ? (
            <div className="bg-white/5 border border-emerald-500/30 rounded-xl p-5 text-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <div className="text-white font-semibold">{t("You're on the list.")}</div>
              <div className="text-slate-400 text-sm mt-1">
                {t("First newsletter drops soon. Check your inbox.")}
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-2.5" data-testid="newsletter-popup-form">
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="First name (optional)"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316]"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
                className="w-full rounded-lg bg-white/10 border border-white/15 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316]"
                data-testid="newsletter-popup-email"
              />
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="w-full rounded-lg py-3 font-bold text-[#0F172A] uppercase tracking-[0.06em] text-sm disabled:opacity-60"
                style={{ background: '#F97316' }}
                data-testid="newsletter-popup-submit"
              >
                {status === 'submitting' ? (
                  <span className="inline-flex items-center gap-2 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> {t("Subscribing...")}</span>
                ) : t("Subscribe free →")}
              </button>
              {error && <div className="text-xs text-red-300 pt-1">{error}</div>}
              <div className="text-[11px] text-slate-500 pt-1 text-center">
                {t("No spam. Unsubscribe with one click.")}
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Sticky bottom bar
// --------------------------------------------------------------------------

export const NewsletterStickyBar = () => {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const { status, error, submit } = useNewsletterSubscribe('sticky_bar');

  useEffect(() => {
    if (isSnoozed(STICKY_STORAGE_KEY)) return;
    // Small delay so the bar doesn't compete with the first paint / popup on the same page.
    const t = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    setVisible(false);
    if (status !== 'success') snooze(STICKY_STORAGE_KEY);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const r = await submit({ email });
    if (r.ok) {
      try { localStorage.setItem(STICKY_STORAGE_KEY, 'signed_up'); } catch { /* ignore */ }
      setTimeout(() => setVisible(false), 2000);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[9998] px-4 pb-4 pointer-events-none"
      data-testid="newsletter-sticky-bar"
    >
      <div className="mx-auto max-w-4xl rounded-xl shadow-2xl pointer-events-auto overflow-hidden"
           style={{ background: 'rgba(15, 23, 42, 0.97)', backdropFilter: 'blur(12px)' }}>
        {status === 'success' ? (
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex items-center gap-2 text-emerald-300 font-medium text-sm">
              <CheckCircle2 className="h-4 w-4" /> {t("You're on the list — first issue drops soon.")}
            </div>
            <button onClick={dismiss} className="text-slate-400 hover:text-white p-1"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <form onSubmit={onSubmit}
                className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 px-4 py-3">
            <div className="flex-1 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#F97316] mb-0.5">
                {t("Monthly lien-law newsletter")}
              </div>
              <div className="text-sm text-slate-200 leading-snug">
                {t("Deadline changes, notice pitfalls, and the plays that get contractors paid.")}
              </div>
            </div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@company.com"
              className="rounded-lg bg-white/10 border border-white/15 px-3 py-2 text-white placeholder:text-slate-400 focus:outline-none focus:border-[#F97316] text-sm sm:min-w-[240px]"
              data-testid="newsletter-sticky-email"
            />
            <button
              type="submit"
              disabled={status === 'submitting'}
              className="rounded-lg px-4 py-2 font-bold text-[#0F172A] uppercase tracking-[0.06em] text-xs disabled:opacity-60"
              style={{ background: '#F97316' }}
              data-testid="newsletter-sticky-submit"
            >
              {status === 'submitting' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Subscribe'}
            </button>
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("Dismiss")}
              className="rounded-lg w-9 h-9 grid place-items-center text-slate-400 hover:text-white hover:bg-white/10"
              data-testid="newsletter-sticky-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </form>
        )}
        {error && status === 'error' && (
          <div className="px-4 pb-2 text-xs text-red-300">{error}</div>
        )}
      </div>
    </div>
  );
};

/**
 * Convenience wrapper — drop `<NewsletterCapture />` on any marketing route
 * to get both surfaces at once. Auto-suppresses on the admin/client apps.
 */
const NewsletterCapture = () => {
  // Never show while inside authenticated apps
  const path = useLocation().pathname;
  if (
    path.startsWith('/admin') ||
    path.startsWith('/dashboard') ||
    path.startsWith('/staff') ||
    path.startsWith('/reset-password') ||
    path.startsWith('/pay/') ||
    path.startsWith('/w8ben/') ||
    path.startsWith('/owner-portal') ||
    path === '/rep' || path.startsWith('/rep/') ||
    path.startsWith('/notary-upload/') ||
    path.startsWith('/login') ||
    path.startsWith('/signup') ||
    path.startsWith('/register')
  ) {
    return null;
  }
  return (
    <>
      {/* Popup intentionally disabled — the sticky bottom bar is the
          only capture surface now. Kept the <NewsletterPopup /> export
          in case we want to reintroduce it on a specific campaign
          landing page (e.g. a lead-magnet PDF gate). */}
      <NewsletterStickyBar />
    </>
  );
};

export default NewsletterCapture;
